// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { fontConfig } from "@bg/XDL/PreStyle/typography";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import translate from "@insite/client-framework/Translate";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    /**
     * If true, the notes will display in a text field for editing.
     * If false, the notes will display as read-only text.
     * Default value: false
     */
    editable?: boolean;
    onNotesChange?: (updatedNotes: string) => void;
    label?: string;
    showAddToList?: boolean;
    addToListHandler?: any;
    showLineNotes?: boolean;
    extendedStyles?: CartLineNotesStyles;
}

type Props = OwnProps & HasCartLineContext;

export interface CartLineNotesStyles {
    wrapper?: InjectableCss;
    toggleLink?: LinkPresentationProps;
    textAreaWrapper?: InjectableCss;
    editableNotesTextArea?: TextAreaProps;
    readOnlyNotesText?: SmallHeadingAndTextStyles;
    addToListLink?: LinkPresentationProps;
}

export const cartLineNotesStyles: CartLineNotesStyles = {
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;

            &&.justify-end {
                justify-content: flex-end;
            }
        `,
    },
    textAreaWrapper: {
        css: css`
            width: 100%;
            margin-top: 5px;
        `,
    },
    addToListLink: {
        typographyProps: {
            size: fontConfig.fontSize.small,
            lineHeight: "17px",
            css: css`
                display: flex;
                opacity: 0.7;
            `,
        },
        icon: {
            iconProps: {
                src: "Plus",
                size: 7,
                color: "text.main",
                css: css`
                    width: 13px;
                    height: 13px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: ${getColor("tertiary.main")};
                    margin-right: 4px;
                `,
            },
            position: "left",
        },
    },
    toggleLink: {
        typographyProps: {
            size: fontConfig.fontSize.xSmall,
            lineHeight: "18px",
        },
        icon: {
            iconProps: {
                src: "Create",
                size: 14,
                color: "secondary.main",
                css: css`
                    margin-right: 4px;
                `,
            },
            position: "left",
        },
    },
    editableNotesTextArea: {
        cssOverrides: {
            inputSelect: css`
                resize: none;
                padding: 8px;
                font-size: ${fontConfig.fontSize.xSmall}px;
                line-height: 18px;
            `,
        },
    },
    readOnlyNotesText: {
        wrapper: {
            css: css`
                padding: 5px;
                background: ${getColor("common.ui4")};
                width: 100%;
                margin-top: 5px;
            `,
        },
    },
};

const CartLineNotes: FC<Props> = ({
    cart,
    cartLine,
    editable = false,
    onNotesChange = () => {},
    label,
    extendedStyles,
    showAddToList,
    addToListHandler,
    showLineNotes,
}) => {
    const [showLineNote, setShowLineNotes] = useState(false);

    const lineNotesClickHandler = () => {
        setShowLineNotes(!showLineNote);
    };

    const [notes, setNotes] = useState(cartLine.notes);

    const changeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNotes(event.currentTarget.value);
    };

    const blurHandler = () => {
        setShowLineNotes(false);
        onNotesChange(notes);
    };

    const [styles] = React.useState(() => mergeToNew(cartLineNotesStyles, extendedStyles));

    const lineLabel = showLineNote ? "Hide Notes" : notes && notes.length ? "Show Notes" : "Line Notes";

    if (editable) {
        return (
            <StyledWrapper {...styles.wrapper} className={!showAddToList ? "justify-end" : ""}>
                {showAddToList && (
                    <Link {...styles.addToListLink} onClick={addToListHandler}>
                        {translate("Project List")}
                    </Link>
                )}
                {showLineNotes && (
                    <Link
                        {...styles.toggleLink}
                        onClick={lineNotesClickHandler}
                        data-test-selector="cartline_notesShowHideButton"
                    >
                        {translate(lineLabel)}
                    </Link>
                )}
                {showLineNote && (
                    <StyledWrapper {...styles.textAreaWrapper}>
                        <TextArea
                            {...styles.editableNotesTextArea}
                            value={notes}
                            disabled={!cart.canModifyOrder}
                            onChange={changeHandler}
                            onBlur={blurHandler}
                            rows={2}
                            maxLength={300}
                            data-test-selector="cartline_notes"
                            id={`notes-${cartLine.id}`}
                        />
                    </StyledWrapper>
                )}
            </StyledWrapper>
        );
    }

    if (!cartLine.notes && !showAddToList) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper} className={!showAddToList ? "justify-end" : ""}>
            {showAddToList && (
                <Link {...styles.addToListLink} onClick={addToListHandler}>
                    {translate("Project List")}
                </Link>
            )}
            {cartLine.notes && (
                <SmallHeadingAndText
                    extendedStyles={styles.readOnlyNotesText}
                    heading={label || translate(" ")}
                    text={cartLine.notes}
                />
            )}
        </StyledWrapper>
    );
};

export default withCartLine(CartLineNotes);
